import { StatesModule, Transition } from '@uirouter/angular';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { FiAbstractStateComponent } from '@fi-shared/fi-state-proxy/fi-abstract-state.component';
import { FiMediaBankListViewStateComponent } from '@fi-app/media-bank/media-bank-list-view-state/media-bank-list-view-state.component';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ListViewSettingsService } from '@fi-app/utilities/fi-services/list-view-settings.service';
import { Ng2StateDeclaration } from '@uirouter/angular';
import { MediaBankNewComponent } from '@fi-app/media-bank/media-bank-new/media-bank-new.component';
import { MediaBankFileModel } from '@fi-app/media-bank/media-bank-file.model';
import { MediaBankViewComponent } from '@fi-app/media-bank/media-bank-view/media-bank-view.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { MediaBankEditComponent } from '@fi-app/media-bank/media-bank-edit/media-bank-edit.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiMediaBankService } from '@fi-shared/media-bank';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

const MediaBankState: Ng2StateDeclaration = {
    name: FiStateNames.MediaBank._name,
    url: '/MediaBank',
    abstract: true,
    component: FiAbstractStateComponent,
    resolve: {
        containerClass: [MediaBankClass],
    },
};
export function MediaBankClass() {
    return 'view-with-inner-header';
}

const MediaBankListState: Ng2StateDeclaration = {
    name: FiStateNames.MediaBank.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:selectedTags&:offset',
    component: FiMediaBankListViewStateComponent,
    reloadOnSearch: false,
    resolve: {
        pageTitle: [PageTitleService, MediaBankListPageTitle],
        mediaListViewSettings: [ListViewSettingsService, MediaBankListMediaListViewSettings],
    },
};
export function MediaBankListPageTitle(ccmPageTitle: PageTitleService) {
    return ccmPageTitle.setTitleByItem(ObjectTypes.MediaBank);
}
export function MediaBankListMediaListViewSettings(SettingsService: ListViewSettingsService) {
    return SettingsService.getSetting(ObjectTypes.MediaBank);
}


const MediaBankNewState: Ng2StateDeclaration = {
    name: FiStateNames.MediaBank.Add,
    url: '/Add?closeIFrameAfterSave',
    views: {
        '@': {
            component: MediaBankNewComponent,
        },
    },
    resolve: {
        pageTitle: [PageTitleService, MediaBankNewPageTitle],
    },
};
export function MediaBankNewPageTitle(ccmPageTitle: PageTitleService) {
    return ccmPageTitle.setTitleByTextId('common.new_file');
}

const MediaBankViewCommentIdResolver = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, $transition$: Transition) => {
        return fiStateService.getParams($transition$).commentId;
    },
];

const MediaBankViewState: Ng2StateDeclaration = {
    name: FiStateNames.MediaBank.View,
    url: '/:Id?commentId&focusNewComment',
    views: {
        '@': {
            component: MediaBankViewComponent,
        },
    },
    params: {
        commentId: undefined,
    },
    resolve: {
        mediaBankFile: [
            FiStateService,
            Transition,
            FiMediaBankService,
            FiFormatDateService,
            MediaBankViewMediaBankFile,
        ],
        pageTitle: [PageTitleService, 'mediaBankFile', MediaBankViewPageTitle],
        focusNewComment: [FiStateService, Transition, MediaBankViewFocusNewComment],
        commentId: MediaBankViewCommentIdResolver,
    },
};
export function MediaBankViewMediaBankFile(
    stateService: FiStateService,
    transition: Transition,
    fiMediaBankService: FiMediaBankService,
    fiFormatDateService: FiFormatDateService,
) {
    const mediaBankFile = new MediaBankFileModel(fiFormatDateService);
    return new Promise<MediaBankFileModel>((resolve, reject) => {
        fiMediaBankService.getMediaBankFile(~~stateService.getParams(transition).Id, true).then((data) => {
            mediaBankFile.Initialize(data, fiFormatDateService);
            resolve(mediaBankFile);
        }, reject);
    });
}
export function MediaBankViewPageTitle(ccmPageTitle: PageTitleService, mediaBankFile: MediaBankFileModel) {
    return ccmPageTitle.setTitleByItem(mediaBankFile);
}

const MediaBankEditState: Ng2StateDeclaration = {
    name: FiStateNames.MediaBank.Edit,
    url: '/:Id/Edit',
    views: {
        '@': {
            component: MediaBankEditComponent,
        },
    },
    resolve: {
        mediaBankFile: [
            FiStateService,
            Transition,
            FiMediaBankService,
            ContentLanguagesService,
            FiFormatDateService,
            MediaBankEditMediaBankFile,
        ],
        pageTitle: [PageTitleService, 'mediaBankFile', MediaBankEditPageTitle],
    },
};
export function MediaBankEditMediaBankFile(
    stateService: FiStateService,
    transition: Transition,
    fiMediaBankService: FiMediaBankService,
    ContentLanguagesService: ContentLanguagesService,
    fiFormatDateService: FiFormatDateService,
) {
    const mediaBankFile = new MediaBankFileModel(fiFormatDateService);
    return new Promise<MediaBankFileModel>((resolve, reject) => {
        fiMediaBankService.getMediaBankFile(~~stateService.getParams(transition).Id, false).then((data) => {
            mediaBankFile.Initialize(data, fiFormatDateService);
            ContentLanguagesService.InitializeContentLanguages(mediaBankFile, data);
            resolve(mediaBankFile);
        }, reject);
    });
}
export function MediaBankEditPageTitle(ccmPageTitle: PageTitleService, mediaBankFile: MediaBankFileModel) {
    return ccmPageTitle.setTitleByItem(mediaBankFile);
}

export const MediaBankStates: StatesModule = {
    states: [MediaBankState, MediaBankListState, MediaBankNewState, MediaBankViewState, MediaBankEditState],
};

export function getMediaBankModulesForLocalSearch() {
    return [
        FiStateNames.MediaBank._name,
        FiStateNames.MediaBank.View,
        FiStateNames.MediaBank.Add,
        FiStateNames.MediaBank.Edit,
    ];
}

export function registerMediaBankStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.MediaBank.List, FiStateNames.MediaBank.View], {
        SettingType: [ApplicationSettingsType.MediaBankCentral, ApplicationSettingsType.MediaBankLocal],
        AccessType: AccessRightType.Read,
    });

    stateAccessCheckService.registerStateRules([FiStateNames.MediaBank.Add, FiStateNames.MediaBank.Edit], {
        SettingType: [ApplicationSettingsType.MediaBankCentral, ApplicationSettingsType.MediaBankLocal],
        AccessType: AccessRightType.Share,
    });
}

export function MediaBankViewFocusNewComment(fiStateService: FiStateService, transition: Transition) {
    return fiStateService.getParams(transition).focusNewComment;
}
